import React, {useEffect, useState} from 'react';

import {MaybeRenderWithSignedURL} from 'external-files/SignedExtFileURLProxy';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as net from 'util/Net';

import {ScratchFileContent} from './ScratchFileTypes';

interface ScratchFileContentLoaderProps {
  signedURL: string;
  fileType: string;
  children: (content: string) => JSX.Element;
}

function ScratchFileContentLoader({
  signedURL,
  fileType,
  children,
}: ScratchFileContentLoaderProps): JSX.Element {
  const [content, setContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    net
      .getPromise(signedURL)
      .then(setContent)
      .catch(() => {
        setError(
          `Failed to load ${fileType} file. Please contact Benchling support if the problem persists.`
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [signedURL, fileType]);

  if (isLoading) {
    return <CenteredHorizontalThrobber />;
  }

  if (error) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={error} />;
  }

  return children(content || '');
}

interface ScratchFileRendererPropsWithLoading {
  scratchFile: ScratchFileContent;
  fileType: string;
  loadContent: true;
  children: (content: string) => JSX.Element;
}

interface ScratchFileRendererPropsWithoutLoading {
  scratchFile: ScratchFileContent;
  fileType: string;
  loadContent: false;
  children: (signedURL: string) => JSX.Element;
}

type ScratchFileRendererProps = ScratchFileRendererPropsWithLoading | ScratchFileRendererPropsWithoutLoading;

export default function ScratchFileRenderer({
  scratchFile,
  fileType,
  loadContent,
  children,
}: ScratchFileRendererProps): JSX.Element {
  return (
    <MaybeRenderWithSignedURL
      externalFileId={scratchFile.externalFileId}
      url={scratchFile.downloadUrl!}
      renderWithSignedURL={(signedURL) => {
        if (signedURL === undefined) {
          return <CenteredHorizontalThrobber />;
        } else if (signedURL == null) {
          return (
            <InlineBanner bannerType={InlineBannerType.ERROR} text={`Error loading ${fileType} file.`} />
          );
        } else {
          if (loadContent) {
            return (
              <ScratchFileContentLoader signedURL={signedURL} fileType={fileType}>
                {children}
              </ScratchFileContentLoader>
            );
          } else {
            return children(signedURL);
          }
        }
      }}
      owningEntityApiId={scratchFile.id}
    />
  );
}
